export interface TimelineSubRowItem {
  uid: string;
  onPageStartTime: number;
  timelineSubRow: number;
}

// Tolerance for floating-point comparisons. Times are rounded to 2 decimal places
// (centiseconds), so 1ms is well below the rounding granularity but large enough
// to absorb IEEE-754 drift from arithmetic on those rounded values.
const OVERLAP_EPSILON = 0.001;

function hasOverlapInRow<T extends TimelineSubRowItem>(item: T, row: number, rowMap: Map<number, T[]>, getEndTime: (item: T) => number): boolean {
  return (rowMap.get(row) ?? []).some(
    (existing) =>
      existing.uid !== item.uid &&
      item.onPageStartTime < getEndTime(existing) - OVERLAP_EPSILON &&
      getEndTime(item) > existing.onPageStartTime + OVERLAP_EPSILON
  );
}

function getHighestRow(items: TimelineSubRowItem[]): number {
  return items.reduce((highestRow, item) => Math.max(highestRow, item.timelineSubRow), -1);
}

function getHighestPlacedRow<T extends TimelineSubRowItem>(rowMap: Map<number, T[]>): number {
  return Math.max(-1, ...rowMap.keys());
}

/**
 * The row nearest the background track that `item` fits on without overlapping what is already
 * placed: the last row is drawn against the background, so the search runs from it upward and
 * opens a fresh last row only when every row collides. `trackHighestRow` is the track's last row
 * before placement, which may hold bars not placed yet — a bar seated there that collides with
 * them re-racks them in turn, so the cascade always runs toward the background. Rows opened by
 * earlier bumps count too, so the fresh row is always below everything placed so far.
 */
function findFreeRowNearestBackground<T extends TimelineSubRowItem>(item: T, trackHighestRow: number, rowMap: Map<number, T[]>, getEndTime: (item: T) => number): number {
  const highestRow = Math.max(trackHighestRow, getHighestPlacedRow(rowMap));
  for (let row = highestRow; row >= 0; row -= 1) {
    if (!hasOverlapInRow(item, row, rowMap, getEndTime)) {
      return row;
    }
  }
  return highestRow + 1;
}

/**
 * The row a bar added to the track should take: the free row nearest the background, or a
 * new last row. New bars therefore appear next to the background rather than at the top.
 */
export function getTimelineSubRowForNewItem<T extends TimelineSubRowItem>(existingItems: T[], newItem: T, getEndTime: (item: T) => number): number {
  const rowMap = new Map<number, T[]>();
  for (const item of existingItems) {
    if (item.uid === newItem.uid) {
      continue;
    }
    rowMap.set(item.timelineSubRow, [...(rowMap.get(item.timelineSubRow) ?? []), item]);
  }
  return findFreeRowNearestBackground(newItem, getHighestRow([...rowMap.values()].flat()), rowMap, getEndTime);
}

/**
 * Re-resolves row assignments after a mutation: overlapping bars on the same row are
 * bumped to the free row nearest the background (a new last row when none fits), then
 * used rows are compacted to remove gaps.
 * `prioritizedUids` (bars a gesture just placed deliberately, e.g. a group drop) claim
 * their contested rows first, so bystanders re-rack around them instead of displacing them.
 */
export function recalculateTimelineSubRows<T extends TimelineSubRowItem>(items: T[], getEndTime: (item: T) => number, prioritizedUids: readonly string[] = []): void {
  if (items.length === 0) {
    return;
  }

  const prioritized = new Set(prioritizedUids);
  const sorted = [...items].sort((a, b) => {
    if (a.timelineSubRow !== b.timelineSubRow) {
      return a.timelineSubRow - b.timelineSubRow;
    }
    if (prioritized.has(a.uid) !== prioritized.has(b.uid)) {
      return prioritized.has(a.uid) ? -1 : 1;
    }
    return a.onPageStartTime - b.onPageStartTime;
  });

  const rowMap = new Map<number, T[]>();
  const highestRow = getHighestRow(items);

  for (const item of sorted) {
    let assignedRow = item.timelineSubRow;

    if (hasOverlapInRow(item, assignedRow, rowMap, getEndTime)) {
      assignedRow = findFreeRowNearestBackground(item, highestRow, rowMap, getEndTime);
    }

    item.timelineSubRow = assignedRow;
    const rowItems = rowMap.get(assignedRow) ?? [];
    rowItems.push(item);
    rowMap.set(assignedRow, rowItems);
  }

  // Compact: remap used rows to eliminate gaps
  const usedRows = [...new Set(items.map((item) => item.timelineSubRow))].sort((a, b) => a - b);
  const rowMapping = new Map<number, number>();
  usedRows.forEach((row, index) => {
    rowMapping.set(row, index);
  });

  for (const item of items) {
    item.timelineSubRow = rowMapping.get(item.timelineSubRow) ?? 0;
  }
}
